/// <amd-module name="Wit/WorkItemForm/Links/Controls/LinkTopologyDiagram" />
import * as React from "react";
import { useEffect, useState } from "react";
import { useService } from "VSS/Platform/Layout";
import { Tooltip } from "VSSUI/TooltipEx";
import { IWorkItemLinksService } from "Wit/Common/WorkItemLinksService/WorkItemLinksService";
import { IWorkItemLinkTypeEnd } from "Wit/WorkItemDataManager/Contracts";
import { useAssetLocation } from "Wit/WorkItemForm/Common/Hooks";
import * as Resources from "Wit/WorkItemForm/Resources";
import { LinkTopology } from "../Constants";

interface ILinkTopologyDiagramProps {
    workItemLinkTypeEnd: string | undefined;
}

const TopologyDiagramAssets = {
    DependencyForward: "ms.vss-work-web/new-work-item-form-content/Content/ltv-dependency-fwd.png",
    DependencyReverse: "ms.vss-work-web/new-work-item-form-content/Content/ltv-dependency-rev.png",
    TreeForward: "ms.vss-work-web/new-work-item-form-content/Content/ltv-tree-fwd.png",
    TreeReverse: "ms.vss-work-web/new-work-item-form-content/Content/ltv-tree-rev.png",
    DirectedNetworkForward: "ms.vss-work-web/new-work-item-form-content/Content/ltv-directed-network-fwd.png",
    DirectedNetworkReverse: "ms.vss-work-web/new-work-item-form-content/Content/ltv-directed-network-rev.png",
    Network: "ms.vss-work-web/new-work-item-form-content/Content/ltv-network.png"
} as const;

interface IAssetDescriptor {
    assetName?: string;
    tooltip?: string;
}

export function LinkTopologyDiagram({ workItemLinkTypeEnd }: ILinkTopologyDiagramProps) {
    const linkService = useService<IWorkItemLinksService>("IWorkItemLinksService");
    const [assetDescriptor, setAssetDescriptor] = useState<IAssetDescriptor>();

    useEffect(() => {
        (async () => {
            const linkEnd = workItemLinkTypeEnd ? await linkService.getWitLinkTypeEnd(workItemLinkTypeEnd) : undefined;
            setAssetDescriptor(getAsset(linkEnd) ?? { assetName: undefined, tooltip: undefined });
        })();
    }, [workItemLinkTypeEnd]);

    const src = useAssetLocation(assetDescriptor?.assetName ?? "");

    return (
        <Tooltip text={assetDescriptor?.tooltip}>
            <img className="link-topology-diagram margin-top-8" alt={assetDescriptor?.tooltip || Resources.LinkTopologyDiagram} src={src} />
        </Tooltip>
    );
}

function getAsset(witLinkTypeEnd: IWorkItemLinkTypeEnd | undefined): { assetName: string; tooltip: string } | undefined {
    const linkType = witLinkTypeEnd?.linkType;
    if (!linkType) {
        return undefined;
    }

    const reverse = linkType.reverseEnd.immutableName === witLinkTypeEnd?.immutableName;

    if (linkType.topology === LinkTopology.Dependency) {
        return reverse
            ? { assetName: TopologyDiagramAssets.DependencyReverse, tooltip: Resources.TopologyDependencyTooltip }
            : { assetName: TopologyDiagramAssets.DependencyForward, tooltip: Resources.TopologyDependencyTooltip };
    }

    if (linkType.topology === LinkTopology.Tree) {
        return reverse
            ? { assetName: TopologyDiagramAssets.TreeReverse, tooltip: Resources.TopologyTreeReverseTooltip }
            : { assetName: TopologyDiagramAssets.TreeForward, tooltip: Resources.TopologyTreeForwardTooltip };
    }

    if (linkType.topology === LinkTopology.DirectedNetwork) {
        return reverse
            ? { assetName: TopologyDiagramAssets.DirectedNetworkReverse, tooltip: Resources.TopologyDirectedNetworkTooltip }
            : { assetName: TopologyDiagramAssets.DirectedNetworkForward, tooltip: Resources.TopologyDirectedNetworkTooltip };
    }

    if (linkType.topology === LinkTopology.Network) {
        return { assetName: TopologyDiagramAssets.Network, tooltip: Resources.TopologyNetworkTooltip };
    }

    return undefined;
}
